import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router';

import { createApiClient } from '@/api/client';
import type { ApiError, LegacyKuduResponseEnvelope } from '@/api/client.types';
import { createLegacyKuduToken } from '@/api/legacyKuduAuth';
import { useAuth } from '@/auth/useAuth';

import { courseQueryKey, type CourseSettings, type RawCourse } from './useCourse';

export interface UpdateCourseHomePayload {
  id: string;
  reordered_component_uids?: string[];
  course_settings?: CourseSettings;
}

export interface BuildUpdateCourseHomePayloadInput {
  courseId: string;
  orderedComponentUids: Array<number | string>;
  orderChanged: boolean;
}

interface RawUpdateCourseHomeResponse {
  course?: RawCourse | null;
}

function createDataError(message: string): ApiError {
  const error = new Error(message) as ApiError;
  error.kind = 'data';
  return error;
}

// Course settings are deliberately NOT part of this payload. UpdateCourse
// replaces the whole CourseSettings message rather than merging fields, and the
// Display Settings dialog now owns them — an edit-mode save carrying a stale
// copy would silently revert whatever the dialog wrote in the meantime.
export function buildUpdateCourseHomePayload({
  courseId,
  orderedComponentUids,
  orderChanged,
}: BuildUpdateCourseHomePayloadInput): UpdateCourseHomePayload {
  return {
    ...(orderChanged
      ? { reordered_component_uids: orderedComponentUids.map(String) }
      : {}),
    id: courseId,
  };
}

export function useUpdateCourseHome(courseId: string | undefined) {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { getIdToken, user } = useAuth();

  return useMutation<
    RawUpdateCourseHomeResponse | LegacyKuduResponseEnvelope<RawUpdateCourseHomeResponse>,
    ApiError,
    UpdateCourseHomePayload
  >({
    mutationFn: async (payload) => {
      if (!user) {
        throw createDataError('You must be signed in to save course settings.');
      }

      const returnTo = `${location.pathname}${location.search}${location.hash}`;
      const client = createApiClient({
        baseUrl: import.meta.env.VITE_API_BASE_URL || '',
        getIdToken,
        getLegacyToken: async () => createLegacyKuduToken(user),
        onAuthError: () =>
          navigate(`/login?returnTo=${encodeURIComponent(returnTo)}`, {
            replace: true,
          }),
      });

      return client.invokeKuduMethod<
        RawUpdateCourseHomeResponse | LegacyKuduResponseEnvelope<RawUpdateCourseHomeResponse>,
        UpdateCourseHomePayload
      >('UpdateCourse', payload);
    },
    onSuccess: async () => {
      if (!courseId) {
        return;
      }

      await queryClient.invalidateQueries({
        queryKey: courseQueryKey(courseId),
      });
    },
  });
}
